import { callWaiter } from 'redux-waiter'
import { financeService } from '@hixme/api'
import epsClient from '../../enrollment-platform/api/eps.client'

import notifications from 'modules/app-core/notification-manager'
import { setModal } from '@hixme/modal'

// project
import { VCN_TRANSACTIONS } from 'modal-constants'

import t from './actionTypes.js'
import * as constants from './constants'
import { normalizeVcnProvider } from './normalizeVcnProvider'
import {
  activateWexVirtualCard,
  createWexVirtualCard,
  getWexVirtualCardsByBenefitId,
  listWexVirtualCardsByEmployeeId,
  suspendWexVirtualCard,
  terminateWexVirtualCard,
  reuseWexVirtualCard,
} from 'enrollment-platform/api/vcn'
import { EMPLOYEE_CARD_UPDATE } from 'routes/Employees/Employee/EmployeeCoverage/modules/employee-benefits/constants'
import { getPartnerVcnWexShowPan } from 'partner-payment-service/api/vcn'
import {
  mapPartnerVcnWexShowPanToFinanceVirtualCard,
  mapWexVcnProvisionToFinanceVirtualCard,
} from './mapWexVcnProvisionToFinanceVirtualCard'

export const loadVirtualCard = (payload) => ({
  type: t.LOAD_VIRTUAL_CARD,
  payload,
})

export const updateVirtualCard = (payload) => ({
  type: t.UPDATE_VIRTUAL_CARD,
  payload,
})

export const viewTransactions = (id) => (dispatch) =>
  dispatch(setModal(VCN_TRANSACTIONS, { id }))

export const loadEmployeeFamilyVirtualCard = (payload) => ({
  type: t.GET_EMPLOYEE_FAMILY_VIRTUAL_CARDS,
  payload,
})

export const getVirtualCard =
  (benefitId, virtualCardType, virtualCardId) => (dispatch) => {
    const provider = normalizeVcnProvider(virtualCardType)
    const req = dispatch(
      callWaiter(`${constants.VCN_GET}-${benefitId}`, {
        requestCreator: () => {
          if (provider === constants.VirtualCardType.WEX) {
            if (!virtualCardId) {
              dispatch(
                notifications.actions.createErrorNotification(
                  'Could not retrieve WEX virtual card: missing virtual card ID.'
                )
              )
              return Promise.reject(
                new Error('Missing WEX virtual card ID for showpan request.')
              )
            }
            return Promise.all([
              getPartnerVcnWexShowPan(virtualCardId),
              // We request card status which is from EPS
              getWexVirtualCardsByBenefitId(benefitId),
            ])
              .then(([showPanRes, epsCards]) => {
                const epsRow = epsCards.find(
                  (c) => c.merchantLogId === virtualCardId
                )
                const virtualCard = mapPartnerVcnWexShowPanToFinanceVirtualCard(
                  showPanRes,
                  epsRow?.status
                )
                dispatch(loadVirtualCard({ ...virtualCard, benefitId }))
                return showPanRes
              })
              .catch((e) => {
                dispatch(
                  notifications.actions.createErrorNotification(
                    'Could not retrieve WEX virtual card.'
                  )
                )
                throw e
              })
          }
          function remapBenefitVirtualCard(raw) {
            const vc = raw.virtualCard || {}
            return {
              VirtualCard: {
                ...vc,
              },
            }
          }
          return epsClient
            .get(
              `core/finances/benefit/${encodeURIComponent(benefitId)}/virtual-card`
            )
            .then(({ data }) => remapBenefitVirtualCard(data))
            .then((res) => {
              dispatch(
                loadVirtualCard({
                  ...res.VirtualCard,
                  benefitId,
                  id: res.VirtualCard.card_public_key,
                  vcnProvider: constants.VirtualCardType.MARQETA,
                })
              )
              return res
            })
            .catch((e) => {
              dispatch(
                notifications.actions.createErrorNotification(
                  'Could not retrieve Marqeta virtual card.'
                )
              )
              throw e
            })
        },
      })
    )
    return req
  }

/**
 * WEX lifecycle actions via EPS (`suspend`, `terminate`, `reactivate` → activate).
 * Uses the same waiter as Marqeta `VCNService.performCardAction` so benefit card UI loading state matches.
 *
 * @param {{ action: import('./constants').WexVirtualCardLifecycleAction, merchantLogId?: string, benefitId?: string }} params
 * @returns {Promise<{ state: string, created_time?: string }>}
 */
export const performWexVirtualCardAction =
  ({ action, merchantLogId, benefitId }) =>
  (dispatch) => {
    const req = dispatch(
      callWaiter(EMPLOYEE_CARD_UPDATE, {
        requestCreator: () => {
          if (!merchantLogId) {
            dispatch(
              notifications.actions.createErrorNotification(
                'Could not update WEX virtual card: missing merchant log ID.'
              )
            )
            return Promise.reject(
              new Error('Missing WEX merchant log ID for lifecycle request.')
            )
          }
          let request
          switch (action) {
            case constants.WexVirtualCardLifecycleAction.SUSPEND:
              request = suspendWexVirtualCard(merchantLogId)
              break
            case constants.WexVirtualCardLifecycleAction.TERMINATE:
              request = terminateWexVirtualCard(merchantLogId, benefitId)
              break
            case constants.WexVirtualCardLifecycleAction.REACTIVATE:
              request = activateWexVirtualCard(merchantLogId)
              break
            default:
              return Promise.reject(
                new Error(`Unsupported WEX VCN action: ${action}`)
              )
          }
          return request
            .then(async (res) => {
              const showpanResult = await getPartnerVcnWexShowPan(
                res.merchantLogId
              )
              const actionResult = mapPartnerVcnWexShowPanToFinanceVirtualCard(
                showpanResult,
                res.status
              )
              dispatch(updateVirtualCard({ ...actionResult }))
              return actionResult
            })
            .catch((e) => {
              dispatch(
                notifications.actions.createErrorNotification(
                  'Could not update WEX virtual card.'
                )
              )
              throw e
            })
        },
      })
    )
    return req
  }

export const reuseVirtualCard =
  ({
    benefitId,
    cardId,
    cardPublicKey,
    userPublicKey,
    virtualCardType,
    merchantLogId,
  }) =>
  (dispatch) => {
    const provider = normalizeVcnProvider(virtualCardType)
    const req = dispatch(
      callWaiter(`${constants.VCN_POST}-${benefitId}`, {
        requestCreator: () => {
          if (provider === constants.VirtualCardType.WEX) {
            if (!merchantLogId) {
              dispatch(
                notifications.actions.createErrorNotification(
                  'Could not reuse WEX virtual card: missing merchant log ID.'
                )
              )
              return Promise.reject(
                new Error('Missing WEX merchant log ID for reuse request.')
              )
            }
            return reuseWexVirtualCard(merchantLogId, benefitId)
              .then(async (res) => {
                const showpanResult = await getPartnerVcnWexShowPan(
                  res.merchantLogId
                )
                const virtualCard = mapPartnerVcnWexShowPanToFinanceVirtualCard(
                  showpanResult,
                  res.status
                )
                dispatch(loadVirtualCard({ ...virtualCard, benefitId }))
                dispatch(updateVirtualCard({ ...virtualCard, benefitId }))
                return res
              })
              .catch((e) => {
                dispatch(
                  notifications.actions.createErrorNotification(
                    'Could not reuse WEX virtual card.'
                  )
                )
                throw e
              })
          }
          return financeService({
            route: `benefit/${benefitId}/virtual-card/reuse`,
            method: 'POST',
            body: {
              VirtualCardId: cardId,
              VirtualCardPublicKey: cardPublicKey,
              UserPublicKey: userPublicKey,
            },
          })
            .then((res) => {
              const withProvider = {
                ...res.VirtualCard,
                benefitId,
                vcnProvider: constants.VirtualCardType.MARQETA,
                id: res.VirtualCard.card_public_key,
              }
              dispatch(loadVirtualCard(withProvider))
              dispatch(updateVirtualCard(withProvider))
              return res
            })
            .catch((e) => {
              dispatch(
                notifications.actions.createErrorNotification(
                  'Could not reuse Marqeta virtual card.'
                )
              )
              throw e
            })
        },
      })
    )
    return req
  }

export const createVirtualCard =
  (benefitId, provider = constants.VirtualCardType.WEX) =>
  (dispatch) => {
    const req = dispatch(
      callWaiter(`${constants.VCN_POST}-${benefitId}`, {
        requestCreator: async () => {
          if (provider === constants.VirtualCardType.WEX) {
            return createWexVirtualCard(benefitId)
              .then(async (res) => {
                let virtualCard
                try {
                  await new Promise((resolve) => setTimeout(resolve, 1000))
                  // get the showpan result from the partner payment service
                  const showpanResult = await getPartnerVcnWexShowPan(
                    res.merchantLogId
                  )
                  virtualCard = mapPartnerVcnWexShowPanToFinanceVirtualCard(
                    showpanResult,
                    res.status
                  )
                } catch {
                  // if the showpan result is not found, use the default mapWexVcnProvisionToFinanceVirtualCard
                  virtualCard = mapWexVcnProvisionToFinanceVirtualCard(res)
                }
                const payload = { ...virtualCard, benefitId }
                dispatch(loadVirtualCard(payload))
                return payload
              })
              .catch((e) => {
                let message = 'Could not create WEX virtual card.'
                if (e.status === 400) {
                  message = e.message
                }
                dispatch(notifications.actions.createErrorNotification(message))
                throw e
              })
          }
          return financeService({
            route: `benefit/${benefitId}/virtual-card`,
            method: 'POST',
          })
            .then((res) => {
              const virtualCard = {
                ...res.VirtualCard,
                benefitId,
                vcnProvider: constants.VirtualCardType.MARQETA,
                id: res.VirtualCard.card_public_key,
              }
              dispatch(loadVirtualCard(virtualCard))
              return virtualCard
            })
            .catch((e) => {
              let message = 'Could not create Marqeta virtual card.'
              if (e.status === 400) {
                message = e.message
              }
              dispatch(notifications.actions.createErrorNotification(message))
              throw e
            })
        },
      })
    )
    return req
  }

function remapApplicationForVcn(raw) {
  if (!raw) return raw
  return {
    ...raw,
    HealthPlanId: raw.healthPlanId ?? raw.HealthPlanId,
  }
}

/**
 * @param {string[]} applicationPublicKeys
 * @returns {Promise<Map<string, string>>} application id → HealthPlanId (HIOS)
 */
async function fetchHealthPlanIdByApplicationId(applicationPublicKeys) {
  const uniqueIds = [...new Set(applicationPublicKeys.filter(Boolean))]
  const entries = await Promise.all(
    uniqueIds.map(async (applicationId) => {
      try {
        const { data: _raw } = await epsClient.get(
          `/core/benefits/applications/${applicationId}`
        )
        const application = remapApplicationForVcn(_raw)
        return [applicationId, application?.HealthPlanId ?? '']
      } catch (e) {
        console.warn(`Could not load application ${applicationId} for HIOS:`, e)
        return [applicationId, '']
      }
    })
  )
  return new Map(entries)
}

function remapFamilyVirtualCardCard(card) {
  const metadata = card.metadata ?? {}
  return {
    ...card,
    card_public_key: card.cardPublicKey ?? card.card_public_key,
    user_public_key: card.userPublicKey ?? card.user_public_key,
    metadata: {
      ...metadata,
      HiosId: metadata.hiosId ?? metadata.HiosId,
    },
  }
}

function remapFamilyVirtualCard(raw) {
  return {
    ...raw,
    Cards: (raw.cards || []).map(remapFamilyVirtualCardCard),
  }
}

// gets all virtual cards for an employee
export const getVirtualCardsForEmployee = (employeePublicKey) => (dispatch) => {
  const req = dispatch(
    callWaiter(`${constants.VCN_GET}-family-cards-${employeePublicKey}`, {
      requestCreator: async () => {
        let availableCards = []
        try {
          const _rawFamilyVcn = await epsClient.get(
            `/core/finances/family/${employeePublicKey}/virtual-card`
          )
          const marqetaVCNListResponse = remapFamilyVirtualCard(
            _rawFamilyVcn.data
          )
          const { Cards } = marqetaVCNListResponse
          availableCards.push(
            ...Cards.map((card) => ({
              id: card.card_public_key,
              cardToken: card.token,
              card: card.pan,
              HIOS: card.metadata.HiosId,
              name: `${card.firstName} ${card.lastName}`,
              userPublicKey: card.user_public_key,
              status: card.state,
              virtualCardType: constants.VirtualCardType.MARQETA,
            }))
          )
        } catch (e) {
          console.warn('Could not load related Marqeta virtual cards:', e)
        }
        try {
          const wexVCNListResponse =
            await listWexVirtualCardsByEmployeeId(employeePublicKey)
          const applicationIds = wexVCNListResponse.map(
            (card) => card.rawData?.userDefinedFields?.benefitId
          )
          const healthPlanIdByApplicationId =
            await fetchHealthPlanIdByApplicationId(applicationIds)
          availableCards.push(
            ...wexVCNListResponse.map((card) => {
              const applicationId = card.rawData?.userDefinedFields?.benefitId
              const HIOS = applicationId
                ? (healthPlanIdByApplicationId.get(applicationId) ?? '')
                : ''
              return {
                id: card.merchantLogId,
                merchantLogId: card.merchantLogId,
                card: card.rawData.virtualCard.number,
                HIOS,
                name: `${card.rawData.userDefinedFields.fullname}`,
                userPublicKey: employeePublicKey,
                status: card.status.toUpperCase(),
                virtualCardType: constants.VirtualCardType.WEX,
              }
            })
          )
        } catch (e) {
          console.warn('Could not load related WEX virtual cards:', e)
        }
        dispatch(loadEmployeeFamilyVirtualCard(availableCards))
      },
    })
  )
  return req
}
